import React from "react";
import {Formik} from "formik";
import {RoleNameEnum} from "../../utils/types/common";

import unflatten from "obj-unflatten";
import {validator} from "@reside/forms";
import {useSelector} from "react-redux";

import {styled, FormGrid, Button, ThinScrollbarArea, Box} from "@reside/ui";

import {AdminPageTitle} from "../admin-layout/AdminPageTitle";
import {domainNamesListToString, domainNamesStringToList} from "./helper";
import {ButtonLink} from "../button-link";
import {CreateOrganizationRequest} from "../../utils/types/organization";
import {AppState, select} from "../../store";
import {Space} from "../space";
import {FormContent} from "./OrganizationFormFields";
import {OrganizationStatusButtons} from "./OrganizationStatusButtons";
import {
  BASE_URL,
  INITIAL_VALUES,
  VALIDATIONS,
  CreateOrUpdateOrganization,
} from "./organizationFormConstants";

const Header = styled(Box)`
  margin-bottom: 15px;
`;

type Props = Readonly<{
  onSubmit: (values: CreateOrUpdateOrganization) => void;
  initialValues?: CreateOrganizationRequest;
  onStatusChange?: () => Promise<void> | void;
  status?: string;
  hasSsoConfig?: {ssoEntryExists: boolean; ssoIsEnabled: boolean};
}>;

export const OrganizationForm = ({
  onSubmit,
  initialValues,
  onStatusChange,
  status,
  hasSsoConfig,
}: Props) => {
  const title = initialValues ? "Edit Organization" : "New Organization";
  const user = useSelector((state: AppState) =>
    select.adminSession.user(state),
  );

  const isResideAdmin = user?.role.name === RoleNameEnum.RESIDE_ADMIN;
  const showDeactivateCheckbox = !!initialValues;

  const allActiveDomainsPresent = (
    values: CreateOrUpdateOrganization,
  ): boolean => {
    if (initialValues?.domains)
      return domainNamesStringToList(initialValues.domains).every(domainName =>
        domainNamesStringToList(values.domains).includes(domainName),
      );
    return true;
  };

  const removedActiveDomains = (values: CreateOrUpdateOrganization): string => {
    if (!initialValues?.domains) return "";
    return domainNamesListToString(
      domainNamesStringToList(initialValues.domains).filter(
        domainName =>
          !domainNamesStringToList(values.domains).includes(domainName),
      ),
    );
  };

  const validate = (values: CreateOrUpdateOrganization) =>
    unflatten(validator.validateAll(values, VALIDATIONS));

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues ?? INITIAL_VALUES}
      onSubmit={onSubmit}
      validate={validate}
    >
      {({submitForm, setFieldValue, values}) => (
        <>
          <Header
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <div>
              <AdminPageTitle>{title}</AdminPageTitle>
            </div>
            <OrganizationStatusButtons
              initialValues={initialValues}
              onStatusChange={onStatusChange}
              status={status}
              hasSsoConfig={hasSsoConfig}
              isResideAdmin={isResideAdmin}
            />
            <Space>
              <ButtonLink to={BASE_URL}>Cancel</ButtonLink>
              <Button color="success" onClick={submitForm}>
                Save
              </Button>
            </Space>
          </Header>
          <ThinScrollbarArea>
            <FormGrid>
              <FormContent
                values={values}
                setFieldValue={setFieldValue}
                initialValues={initialValues}
                showDeactivateCheckbox={showDeactivateCheckbox}
                allActiveDomainsPresent={allActiveDomainsPresent}
                removedActiveDomains={removedActiveDomains}
              />
            </FormGrid>
          </ThinScrollbarArea>
        </>
      )}
    </Formik>
  );
};
